// Fee & Obligation PR-5 — the Income Pools section: booked-to-date beside
// expected-ahead. The booked matrix REUSES the Financials redesign's
// IIncomePoolsReadService fold (via useLoanIncomePools) — it is NOT re-folded
// here. The forward overlay is the additive PR-5 piece: unpaid vendor invoices
// project into Pool B (VFI), the projected interest spread into Pool G
// (Phoenix Interest & Service Income, non-commissionable — surfaced off the
// isCommissionable flag).

import { useMemo } from "react";
import { Button, Card, EmptyState, Skeleton } from "@/design-system/components/ui";
import { formatCurrency } from "@/design-system/lib/format";
import { DataTable, type Column } from "@/components/layout/DataTable";
import { useLoanIncomePools } from "@/hooks/portfolioAdmin/useIncomePools";
import { useIncomePoolsForward } from "@/hooks/portfolioAdmin/useForwardProjection";
import type {
  IncomePoolColumnDto,
  IncomePoolsPeriodRowDto,
} from "@/api/portfolioAdmin/incomePoolsApi";
import { formatPoolPeriod } from "./IncomePoolsPanel";

function cellAmount(row: IncomePoolsPeriodRowDto, poolCode: string): number {
  return row.pools.find((c) => c.poolCode === poolCode)?.amount ?? 0;
}

function money(value: number): string {
  return formatCurrency(value, { maximumFractionDigits: 2 });
}

function PoolMatrix({
  pools,
  periods,
  emptyTitle,
  emptyDescription,
  testId,
}: {
  pools: IncomePoolColumnDto[];
  periods: IncomePoolsPeriodRowDto[];
  emptyTitle: string;
  emptyDescription: string;
  testId: string;
}) {
  const visiblePools = useMemo(
    () => pools.filter((p) => periods.some((row) => cellAmount(row, p.code) !== 0)),
    [pools, periods],
  );

  const columns = useMemo<Column<IncomePoolsPeriodRowDto>[]>(() => {
    const poolColumns: Column<IncomePoolsPeriodRowDto>[] = visiblePools.map((p) => ({
      key: `pool-${p.code}`,
      header: p.isCommissionable ? p.code : `${p.code} · non-commissionable`,
      className: "text-right tabular-nums",
      render: (row) => {
        const amount = cellAmount(row, p.code);
        return amount !== 0 ? money(amount) : "—";
      },
    }));
    return [
      { key: "period", header: "Period", render: (row) => formatPoolPeriod(row.periodStart) },
      ...poolColumns,
      {
        key: "total",
        header: "Total",
        className: "text-right tabular-nums font-semibold",
        render: (row) => money(row.total),
      },
    ];
  }, [visiblePools]);

  if (periods.length === 0) {
    return <EmptyState title={emptyTitle} description={emptyDescription} />;
  }
  return (
    <DataTable
      columns={columns}
      rows={periods}
      rowKey={(row) => row.periodStart}
      data-testid={testId}
    />
  );
}

export function IncomePoolsForwardSection({ loanId }: { loanId: string }) {
  const booked = useLoanIncomePools(loanId);
  const forward = useIncomePoolsForward(loanId);

  const isLoading = booked.isLoading || forward.isLoading;
  const isError = booked.isError || forward.isError;

  const legendPools = useMemo<IncomePoolColumnDto[]>(() => {
    const byCode = new Map<string, IncomePoolColumnDto>();
    for (const p of booked.data?.pools ?? []) byCode.set(p.code, p);
    for (const p of forward.data?.pools ?? []) if (!byCode.has(p.code)) byCode.set(p.code, p);
    return [...byCode.values()];
  }, [booked.data, forward.data]);

  if (isLoading) {
    return (
      <Card data-testid="income-pools-section-loading">
        <Skeleton className="h-8 w-1/2" />
        <Skeleton className="h-5 w-full mt-space-3" />
      </Card>
    );
  }

  if (isError || !booked.data || !forward.data) {
    return (
      <Card data-testid="income-pools-section-error">
        <p className="text-small text-status-error m-0 mb-space-2">
          Income Pools could not be loaded.
        </p>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => {
            void booked.refetch();
            void forward.refetch();
          }}
        >
          Retry
        </Button>
      </Card>
    );
  }

  return (
    <Card data-testid="income-pools-section">
      <h3 className="font-display font-bold text-h3 m-0 mb-space-1">Income Pools</h3>
      <p className="text-small text-gray-mid m-0 mb-space-3">
        Phoenix income per period, by revenue pool — what has booked to date beside
        what the projection expects ahead. View-only.
      </p>

      <div className="grid grid-cols-1 gap-space-4 xl:grid-cols-2">
        <div className="flex flex-col gap-space-2">
          <span className="text-small font-semibold text-onyx">Booked to date</span>
          <PoolMatrix
            pools={booked.data.pools}
            periods={booked.data.periods}
            emptyTitle="Nothing booked yet"
            emptyDescription="Pool income books as the loan's payments settle."
            testId="income-pools-booked-table"
          />
        </div>

        <div className="flex flex-col gap-space-2">
          <span className="text-small font-semibold text-onyx">Expected ahead (projected)</span>
          <PoolMatrix
            pools={forward.data.pools}
            periods={forward.data.periods}
            emptyTitle="Nothing projected ahead"
            emptyDescription="No unpaid vendor fees or projected interest income lie ahead."
            testId="income-pools-forward-table"
          />
        </div>
      </div>

      {legendPools.length > 0 && (
        <dl className="m-0 mt-space-3 text-caption text-gray-mid" data-testid="income-pools-section-legend">
          {legendPools.map((p) => (
            <div key={p.code} className="flex gap-space-2">
              <dt className="m-0 font-semibold">{p.code}</dt>
              <dd className="m-0">
                {p.name}
                {!p.isCommissionable && " — non-commissionable"}
                {p.isRetired && " (retired)"}
              </dd>
            </div>
          ))}
        </dl>
      )}
    </Card>
  );
}
